import React, { useCallback, useEffect, useRef, useState } from "react";
import { Responsive, WidthProvider } from "react-grid-layout";
import { useSelector, useDispatch } from "react-redux";
import TradeChart from "./TradingChart/TradeChart.jsx";
import OpenOrder from "./OpenOrder/OpenOrder.jsx";
import MarketTrades from "./MarketTrades/MarketTrades.jsx";
import AdjustmentTools from "../../components/common/AdjustmentTools";
import BuySellTab from "./BuySellTab/BuySellTab.jsx";
// import _ from "lodash";
import { saveChangesGridLayout, saveCurrentBreakPoint, saveMobileTab, saveOldGridLayout, resetLayout, saveChangesGridList, getUserLayout } from "../../features/persist/persistSlice";
// import { GRID_LAYOUT } from "../../constants/grid.layout";
import Accounts from "./Accounts/Accounts.jsx";
import WatchList from "./WatchList/WatchList.jsx";
import OrderBook from "./OrderBook/OrderBook.jsx";
import { parseJSON } from "../../helpers/commonHelper";
import { normalizeTradeGridLayout } from "../../constants/grid.layout";
import { applyBuySellTabAutoHeight, applyViewportBottomFill, cloneGridLayouts, GRID_MARGIN_X, GRID_MARGIN_Y, GRID_ROW_HEIGHT } from "./gridMetrics";
import { useBuySellTabAutoHeight } from "./hooks/useBuySellTabAutoHeight";
import { useTradeGridViewportFill } from "./hooks/useTradeGridViewportFill";
const ReactGridLayout = WidthProvider(Responsive);
const ResponsiveExchange = ({
  currentLayout
}) => {
  const BuySellTabRef = useRef(null);
  const [buySellContentEl, setBuySellContentEl] = useState(null);
  const handleBuySellTabRef = useCallback(node => {
    BuySellTabRef.current = node;
    setBuySellContentEl(node);
  }, []);
  const AccountsRef = useRef(null);
  const TradeChartRef = useRef(null);
  const OrderBookRef = useRef(null);
  const WatchListRef = useRef(null);
  const OpenOrderRef = useRef(null);
  const MarketTradesRef = useRef(null);
  const components = React.useMemo(() => ({
    BuySellTab: <BuySellTab sectionRef={handleBuySellTabRef} />,
    Accounts: <Accounts sectionRef={AccountsRef} />,
    TradeChart: <TradeChart sectionRef={TradeChartRef} />,
    OrderBook: <OrderBook sectionRef={OrderBookRef} />,
    WatchList: <WatchList sectionRef={WatchListRef} />,
    OpenOrder: <OpenOrder sectionRef={OpenOrderRef} />,
    MarketTrades: <MarketTrades sectionRef={MarketTradesRef} />
  }), [handleBuySellTabRef]);
  const gridLayout = useSelector(state => state.persist.gridLayout);
  const layoutList = useSelector(state => state.persist.layoutList);
  const oldGridLayout = useSelector(state => state.persist.old_grid_layout);
  const mobile_tab = useSelector(state => state.persist.mobile_tab);
  const current_breakpoint = useSelector(state => state.persist.current_breakpoint);
  const current_workspace = useSelector(state => state.persist.current_workspace);
  const resolvedLayouts = React.useMemo(() => normalizeTradeGridLayout(parseJSON(gridLayout)), [gridLayout]);
  const isDesktopBreakpoint = current_breakpoint === "lg" || current_breakpoint === "md";
  const [layoutAutoFit, setLayoutAutoFit] = useState(true);
  const [manualBuySellH, setManualBuySellH] = useState(null);
  const buySellTabGridH = useBuySellTabAutoHeight(buySellContentEl, {
    enabled: isDesktopBreakpoint
  });
  const viewportFillBottomH = useTradeGridViewportFill({
    enabled: isDesktopBreakpoint && layoutAutoFit
  });
  const effectiveBuySellH = React.useMemo(() => {
    if (!buySellTabGridH) return manualBuySellH;
    if (manualBuySellH == null) return buySellTabGridH;
    return Math.max(buySellTabGridH, manualBuySellH);
  }, [buySellTabGridH, manualBuySellH]);
  useEffect(() => {
    if (buySellTabGridH && manualBuySellH != null && buySellTabGridH > manualBuySellH) {
      setManualBuySellH(null);
    }
  }, [buySellTabGridH, manualBuySellH]);
  const layoutsForGrid = React.useMemo(() => {
    // RGL mutates layout item x/y during drag — always pass a deep clone.
    let base = cloneGridLayouts(resolvedLayouts);
    if (effectiveBuySellH && isDesktopBreakpoint) {
      base = applyBuySellTabAutoHeight(base, effectiveBuySellH);
    }
    if (layoutAutoFit && viewportFillBottomH && isDesktopBreakpoint) {
      base = applyViewportBottomFill(base, viewportFillBottomH);
    }
    return base;
  }, [resolvedLayouts, effectiveBuySellH, viewportFillBottomH, layoutAutoFit, isDesktopBreakpoint]);
  const skipLayoutPersistRef = useRef(false);
  const updateLayout = async layouts => {
    await getUserLayout("PUT", {
      layouts
    });
  };
  const toggleFullScreen = i => {
    let secRef = BuySellTabRef;
    switch (i) {
      case "Accounts":
        secRef = AccountsRef;
        break;
      case "TradeChart":
        secRef = TradeChartRef;
        break;
      case "OrderBook":
        secRef = OrderBookRef;
        break;
      case "WatchList":
        secRef = WatchListRef;
        break;
      case "OpenOrder":
        secRef = OpenOrderRef;
        break;
      case "MarketTrades":
        secRef = MarketTradesRef;
        break;
      default:
        break;
    }
    if (!document.fullscreenElement) {
      secRef.current.requestFullscreen().catch(err => {
        console.error(`Error attempting to enable full-screen mode: ${err.message}`);
      });
    } else {
      document.exitFullscreen();
    }
  };
  let props = {
    className: "layout",
    isDraggable: true,
    isResizable: true,
    onLayoutChange: async function (layout, layouts) {
      const current = parseJSON(layoutList);
      const index = current.findIndex(o => o.active);
      if (index < 0) return;
      // Save full layouts object so drag/resize on all breakpoints persists.
      current[index].layout = parseJSON(layouts);
      dispatch(saveChangesGridList(current));
      await updateLayout(current);
    },
    breakpoints: {
      lg: 1480,
      md: 1149,
      sm: 767,
      xs: 480
    },
    cols: {
      lg: 30,
      md: 30,
      sm: 30,
      xs: 30
    },
    rowHeight: GRID_ROW_HEIGHT,
    margin: [GRID_MARGIN_X, GRID_MARGIN_Y],
    draggableCancel: ".nondraggable_card",
    autoSize: true,
    draggableHandle: ".drag-handle"
  };
  const dispatch = useDispatch();
  const [isExpand, setIsExpand] = useState(false);
  const [isInitial, setIsInitial] = useState(false);
  useEffect(() => {
    if (oldGridLayout !== undefined && oldGridLayout !== null) {
      if (Object.keys(oldGridLayout).length > 0) {
        dispatch(saveChangesGridLayout(oldGridLayout));
        dispatch(saveOldGridLayout({}));
      }
    }
  }, []);
  const hanldeResetLayout = () => {
    dispatch(saveMobileTab("MARKET"));
    dispatch(resetLayout());
    setLayoutAutoFit(true);
    setManualBuySellH(null);
  };
  const onBreakpointChange = breakpoint => {
    if (breakpoint === "lg" || breakpoint === "md") {
      if (isInitial) {
        hanldeResetLayout();
      }
    }
    if (current_breakpoint !== breakpoint) {
      dispatch(saveCurrentBreakPoint(breakpoint));
    }
    setIsInitial(true);
  };
  const onLayoutChange = (layout, layouts) => {
    if (skipLayoutPersistRef.current) return;
    const buySellItem = layout.find(item => item.i === "BuySellTab");
    if (buySellItem && buySellTabGridH && buySellItem.h > buySellTabGridH + 0.15) {
      setManualBuySellH(buySellItem.h);
    }
    setLayoutAutoFit(false);

    // Persist the layout RGL just produced (clone so Redux freeze can't affect RGL).
    const nextLayouts = cloneGridLayouts(layouts);
    if (!isExpand) {
      if (current_breakpoint === "lg" || current_breakpoint === "md") {
        dispatch(saveChangesGridLayout(nextLayouts));
      }
    }
    props.onLayoutChange(layout, nextLayouts);
  };
  const onRemoveItem = i => {
    console.log('Hello World -> ', i);
    const layouts = parseJSON(layoutList);
    const grid_layout = parseJSON(gridLayout);
    const index = layouts.findIndex(o => o.active);
    let lg_grid_layout = grid_layout?.lg;
    let md_grid_layout = grid_layout?.md;
    const lg_grid_index = lg_grid_layout.findIndex(doc => doc.i === i);
    const md_grid_index = md_grid_layout.findIndex(doc => doc.i === i);
    if (lg_grid_layout) lg_grid_layout.splice(lg_grid_index, 1);
    if (md_grid_layout) md_grid_layout.splice(md_grid_index, 1);

    // let lg_grid_layout = gridLayout?.lg?.slice();
    // let md_grid_layout = gridLayout?.md?.slice();
    // let sm_grid_layout = gridLayout?.sm?.slice();
    // let xs_grid_layout = gridLayout?.xs?.slice();

    // const lg_index = lg_grid_layout.findIndex((doc) => doc.i === i);
    // const md_index = md_grid_layout.findIndex((doc) => doc.i === i);
    // // const sm_index = sm_grid_layout.findIndex((doc) => doc.i === i);
    // // const xs_index = xs_grid_layout.findIndex((doc) => doc.i === i);

    // if (lg_grid_layout) lg_grid_layout.splice(lg_index, 1);
    // if (md_grid_layout) md_grid_layout.splice(md_index, 1);
    // // if (sm_grid_layout) sm_grid_layout.splice(sm_index, 1);
    // // if (xs_grid_layout) xs_grid_layout.splice(xs_index, 1);

    let new_grid_layout = {
      lg: lg_grid_layout,
      md: md_grid_layout
      // sm: sm_grid_layout,
      // xs: xs_grid_layout,
    };
    layouts[index].layout = new_grid_layout;
    dispatch(saveChangesGridLayout(new_grid_layout));
    dispatch(saveChangesGridList(layouts));
  };
  const onExtendItem = i => {
    dispatch(saveOldGridLayout(gridLayout));
    setIsExpand(true);
    const newLayout = [{
      i: i,
      x: 0,
      y: 0,
      w: 24,
      h: 29
    }, {
      i: "BuySellTab",
      x: 0,
      y: 0,
      h: 10.35,
      w: 6,
      isResizable: true,
      resizeHandles: ["se"]
    }, {
      i: "WatchList",
      x: 24,
      y: 1,
      h: 5,
      w: 6,
      isResizable: false
    }];
    if (current_breakpoint === "lg" || current_breakpoint === "md") {
      let currentLayout = {
        ...gridLayout
      };
      currentLayout[current_breakpoint] = newLayout;
      console.log({
        currentLayout
      });
      dispatch(saveChangesGridLayout(currentLayout));
    }
  };
  const onReduceItem = i => {
    setIsExpand(false);
    dispatch(saveChangesGridLayout(oldGridLayout));
    dispatch(saveOldGridLayout({}));
  };
  const showResponsiveView = (blockType, currentPoint) => {
    if (currentPoint === "sm" || currentPoint === "xs") {
      switch (blockType) {
        case "TradeChart":
          {
            if (mobile_tab === "TRADE") {
              return true;
            }
            return false;
          }
        case "OpenOrder":
          {
            if (mobile_tab === "TRADE") {
              return true;
            }
            return false;
          }
        case "BuySellTab":
          {
            if (mobile_tab === "BUYSELL") {
              return true;
            }
            return false;
          }
        case "Accounts":
          {
            if (mobile_tab === "BUYSELL") {
              return true;
            }
            return false;
          }
        case "OrderBook":
          {
            if (mobile_tab === "BOOK") {
              return true;
            }
            return false;
          }
        case "WatchList":
          {
            if (mobile_tab === "MARKET") {
              return true;
            }
            return false;
          }
        case "MarketTrades":
          {
            if (mobile_tab === "MARKET") {
              return true;
            }
            return false;
          }
        default:
          return false;
      }
    } else {
      return true;
    }
  };
  useEffect(() => {
    if (!isDesktopBreakpoint) return undefined;
    skipLayoutPersistRef.current = true;
    const frame = requestAnimationFrame(() => {
      skipLayoutPersistRef.current = false;
    });
    return () => cancelAnimationFrame(frame);
  }, [effectiveBuySellH, viewportFillBottomH, layoutAutoFit, isDesktopBreakpoint]);
  useEffect(() => {
    setLayoutAutoFit(true);
    setManualBuySellH(null);
  }, [current_workspace]);
  const generateDOM = () => {
    const layoutObject = [];
    gridLayout[current_breakpoint === "sm" || current_breakpoint === "xs" ? "lg" : current_breakpoint]?.map(item => {
      const isComponent = showResponsiveView(item.i, current_breakpoint);
      if (isComponent) {
        layoutObject.push(<div key={item.i} className={`mainCard  ${item.i}`}>
            {(current_breakpoint === "lg" || current_breakpoint === "md") && <div className="head_move">
                <AdjustmentTools name={item.i} onClose={onRemoveItem} onExtend={toggleFullScreen} onReduce={onReduceItem} />
              </div>}
            <div className={`mainCardbody nondraggable_card`}>
              {components[item.i]}
            </div>
          </div>);
      }
    });
    return layoutObject;
  };
  return <>
      <ReactGridLayout {...props} layouts={layoutsForGrid} onBreakpointChange={onBreakpointChange} onLayoutChange={onLayoutChange}>
        {generateDOM()}
      </ReactGridLayout>
    </>;
};
export default ResponsiveExchange;